/* eslint-disable react-hooks/exhaustive-deps */
import localforage from "localforage";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import * as assetStorage from "../services/assetStorage";
import {
  CLIENTS_KEY,
  CLIENT_FORM_KEY,
  COMPANY_KEY,
  PRODUCTS_KEY,
  PRODUCT_FORM_KEY,
  // APP_CONTEXT,
  INVOICE_DETAILS,
  INVOICES_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_COLOR,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_COLOR,
  DEFAULT_QUOTATION_BG,
} from "../constants/localKeys";
import { useAppContext } from "../context/AppContext";
import { updateNewClientForm, setAllClients } from "../store/clientSlice";
import { updateCompanyData } from "../store/companySlice";
import {
  setAllInvoice,
  setAllInvoiceDetailList,
  updateNewInvoiceForm,
} from "../store/invoiceSlice";
import {
  setAllQuotation,
  setAllQuotationDetailList,
  setNewForm as setNewQuotationForm,
} from "../store/quotationSlice";
import { setAllProducts, updateNewProductForm } from "../store/productSlice";
import SubscriptionLimitValidator from "../utils/subscriptionLimitValidator";
import subscriptionService from "../services/subscriptionService";
import userService from "../services/userService";

const useInitApp = () => {
  const dispatch = useDispatch();
  const { setInitLoading } = useAppContext();
  
  // Get current invoice details from Redux state
  // const currentInvoiceDetails = useSelector(getAllInvoiceDetailSelector);

  // NOTE: currentInvoiceDetails is intentionally omitted from dependencies to prevent infinite loops
  // We access its current value inside the callback without needing to recreate the function
  const initialSetData = useCallback(async () => {
    console.log('🚀 Starting app initialization...');
    const startTime = Date.now();
    try {
      console.log('📦 Loading data from storage...');

      // Pull the latest subscription plans from the DB so admin edits are
      // reflected on the user-facing pricing UI without a redeploy. Fire and
      // forget — getSubscriptionPlans() falls back to defaults until this
      // resolves, so we don't need to block app init on it.
      subscriptionService.loadPlansFromDB().catch(() => {})

      // Check subscription expiry at app startup
      const user = userService.getCurrentUser()
      if (user && user.subscription_tier !== 'free') {
        // This will automatically handle expiry and downgrade if needed
        subscriptionService.isSubscriptionActive()
      }

      // Add timeout to individual storage operations
      const withTimeout = (promise, ms = 3000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error(`Storage operation timeout`)), ms))
        ]);
      };

      const [
        companyData,
        clientNewForm,
        clients,
        productNewForm,
        products,
        invoices,
        invoiceDetailList,
        invoiceNewForm,
        defaultColor,
        defaultBackground,
        quotations,
        quotationDetailList,
        quotationNewForm,
        defaultQuotationColor,
        defaultQuotationBackground,
      // Image-carrying keys go through assetStorage which expands $ref
      // placeholders back into the inlined base64 the downstream Redux
      // store + PDF renderer expect. Scalar keys (color constants) still
      // use localforage directly to avoid paying the wrapper overhead.
      ] = await Promise.all([
        withTimeout(assetStorage.getItem(COMPANY_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(CLIENT_FORM_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(CLIENTS_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(PRODUCT_FORM_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(PRODUCTS_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(INVOICES_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(INVOICE_DETAILS)).catch(() => null),
        withTimeout(assetStorage.getItem(INVOICE_FORM_KEY)).catch(() => null),
        withTimeout(localforage.getItem(DEFAULT_INVOICE_COLOR)).catch(() => null),
        withTimeout(assetStorage.getItem(DEFAULT_INVOICE_BG)).catch(() => null),
        withTimeout(assetStorage.getItem(QUOTATIONS_KEY)).catch(() => null),
        withTimeout(assetStorage.getItem(QUOTATION_DETAILS)).catch(() => null),
        withTimeout(assetStorage.getItem(QUOTATION_FORM_KEY)).catch(() => null),
        withTimeout(localforage.getItem(DEFAULT_QUOTATION_COLOR)).catch(() => null),
        withTimeout(assetStorage.getItem(DEFAULT_QUOTATION_BG)).catch(() => null),
      ]);
      console.log('✅ Data loaded from storage, processing...');

      if (companyData) {
        console.log('🏢 Loading company data');
        dispatch(updateCompanyData(companyData));
      }

      if (clientNewForm) {
        dispatch(updateNewClientForm(clientNewForm));
      }

      if (clients) {
        console.log('👥 Loading clients data');
        // Validate and enforce subscription limits on clients
        const validatedClients = SubscriptionLimitValidator.validateResourceLimit('clients', clients);
        dispatch(setAllClients(validatedClients));
      }

      if (productNewForm) {
        dispatch(updateNewProductForm(productNewForm));
      }

      if (products) {
        // Validate and enforce subscription limits on products
        const validatedProducts = SubscriptionLimitValidator.validateResourceLimit('products', products);
        dispatch(setAllProducts(validatedProducts));
      }

      if (invoiceNewForm) {
        dispatch(updateNewInvoiceForm(invoiceNewForm));
      }

      if (invoices) {
        // Validate and enforce subscription limits on invoices
        const validatedInvoices = SubscriptionLimitValidator.validateResourceLimit('invoices', invoices);
        console.log('Loading invoices data:', {
          originalCount: invoices?.length || 0,
          validatedCount: validatedInvoices?.length || 0,
          sampleInvoices: validatedInvoices?.slice(0, 3)?.map(inv => ({ id: inv.id, invoiceNo: inv.invoiceNo })) || []
        });
        dispatch(setAllInvoice(validatedInvoices));
      }

      if (invoiceDetailList) {
        // Validate and enforce subscription limits on invoice details
        const validatedInvoiceDetails = SubscriptionLimitValidator.validateResourceLimit('invoices', invoiceDetailList);
        console.log('Loading invoice details data:', {
          originalCount: invoiceDetailList?.length || 0,
          validatedCount: validatedInvoiceDetails?.length || 0,
          sampleDetails: validatedInvoiceDetails?.slice(0, 3)?.map(inv => ({ id: inv.id, invoiceNo: inv.invoiceNo, hasProducts: !!inv.products?.length })) || []
        });
        dispatch(setAllInvoiceDetailList(validatedInvoiceDetails));
      }
      
      // TEMPORARILY DISABLED: Data consistency check (causing hangs)
      console.log('⚠️ Data consistency check disabled to prevent hangs');

      if (quotationNewForm) {
        dispatch(setNewQuotationForm(quotationNewForm));
      }

      if (quotations) {
        // Validate and enforce subscription limits on quotations
        const validatedQuotations = SubscriptionLimitValidator.validateResourceLimit('invoices', quotations); // Using same limit as invoices
        console.log('Loading quotations data:', {
          originalCount: quotations?.length || 0,
          validatedCount: validatedQuotations?.length || 0,
          sampleQuotations: validatedQuotations?.slice(0, 3)?.map(quot => ({ id: quot.id, quotationNo: quot.quotationNo })) || []
        });
        dispatch(setAllQuotation(validatedQuotations));
      }

      if (quotationDetailList) {
        // Validate and enforce subscription limits on quotation details
        const validatedQuotationDetails = SubscriptionLimitValidator.validateResourceLimit('invoices', quotationDetailList); // Using same limit as invoices
        console.log('Loading quotation details data:', {
          originalCount: quotationDetailList?.length || 0,
          validatedCount: validatedQuotationDetails?.length || 0,
          sampleDetails: validatedQuotationDetails?.slice(0, 3)?.map(quot => ({ id: quot.id, quotationNo: quot.quotationNo, hasProducts: !!quot.products?.length })) || []
        });
        dispatch(setAllQuotationDetailList(validatedQuotationDetails));
      }

      if (defaultColor) {
      }

      if (defaultBackground) {
      }

      if (defaultQuotationColor) {
      }

      if (defaultQuotationBackground) {
      }


    } catch (e) {
      console.error('❌ App initialization error:', e);
      // Silent - app initialization errors should not expose details
    } finally {
      const endTime = Date.now();
      console.log(`✅ App initialization complete in ${endTime - startTime}ms, setting initLoading to false`);
      setInitLoading(false);
    }
  }, [dispatch, setInitLoading]);

  return {
    initialSetData,
  };
};

export default useInitApp;

// DEBUG: Added console log
